/**
 * 本地假桥：让页面能脱离 App 在浏览器里单独跑。
 *
 * 只在 **dev 构建** 或 URL 带 `?mock=1` 时安装；生产构建是空操作。
 * 它伪装成 Flutter 端（提供 `flutter_inappwebview.callHandler`），
 * 所以 `bridge.platform === 'android'`，页面走真实分支。
 *
 * 备份/迁移会**按真实语义推 `*.progress` 事件**，方便看进度与状态流转。
 *
 * 用法：`bun run dev` 然后打开 `http://localhost:5173/?mock=1`。
 */

import { BridgeError } from './bridge';

type MockHandler = (params: Record<string, unknown>) => unknown;

const MOCK_ACCOUNTS = [
  { id: 'mock-1', nickname: '演示账号 A', avatar: '', canWrite: true, appId: 'bxy',
    capabilities: ['postsBackup', 'postsVisibility', 'postsDelete'] },
  { id: 'mock-2', nickname: '演示账号 B', avatar: '', canWrite: false, appId: 'bxy' },
  // 薯薯小红书账号：百宝箱工具页用 `?mock=1&accountId=mock-xhs`
  { id: 'mock-xhs', nickname: '演示小红书号', avatar: '', canWrite: true, appId: 'shushu', platform: 'xiaohongshu',
    platformLabel: '小红书', handle: 'demo_xhs', uid: '5f1a2b3c000000000101d8e2',
    capabilities: ['postsBackup', 'commentsDelete', 'commentsBackup', 'accountLookup', 'collectsExport', 'mediaDownload', 'interactionsClean'] },
];

/** 假的小红书 feed（`xhs.request` feed 返回的 data）：一篇 3 图图文，第 2 张是实况图 */
function mockFeed(noteId: string) {
  const pic = (seed: number) => ({ info_list: [{ image_scene: 'WB_DFT', url: `https://picsum.photos/seed/xhs${seed}/600/800` }] });
  return {
    items: [{
      note_card: {
        note_id: noteId, type: 'normal', title: '演示笔记：周末去哪儿', desc: '正文 #旅行[话题]#',
        user: { user_id: '5f1a2b3c000000000101d8e2', nickname: '演示作者', avatar: '' },
        image_list: [pic(1), { ...pic(2), live_photo: true, stream: { h264: [{ master_url: 'https://example.com/live.mp4' }] } }, pic(3)],
      },
    }],
  };
}

let mockLikesLeft = 6;
let mockFollowsLeft = 4;

/** 评论备份：5 篇，每 0.6 秒完成一篇 */
let mockCommentNotes = 2;
let mockCommentTask: Record<string, unknown> | null = null;
function mockCommentBackup(accountId: string) {
  return mockCommentTask ?? { state: 'idle', accountId };
}
function startMockCommentBackup(accountId: string) {
  mockCommentTask = { accountId, taskId: 'mock-comments', state: 'running', total: 5, done: 0, failed: 0, comments: 0, current: '演示笔记 1' };
  for (let i = 1; i <= 5; i++) {
    window.setTimeout(() => {
      const last = i === 5;
      mockCommentTask = {
        ...mockCommentTask!, done: i, comments: i * 9, current: last ? undefined : `演示笔记 ${i + 1}`,
        state: last ? 'completed' : 'running',
      };
      if (last) mockCommentNotes = 5;
      window.__bridgeEmit?.({ event: 'comments.progress', payload: mockCommentTask });
    }, i * 600);
  }
  return mockCommentTask;
}

/** 假的收藏列表：共 75 条，每页 30 */
function mockCollectPage(cursor: string) {
  const start = cursor ? Number(cursor) : 0;
  const notes = Array.from({ length: Math.min(30, 75 - start) }, (_, i) => ({
    note_id: `64${String(start + i).padStart(22, '0')}`, display_title: `收藏笔记 ${start + i + 1}`,
    type: (start + i) % 4 === 0 ? 'video' : 'normal', xsec_token: 'demo', user: { nickname: `作者 ${(start + i) % 7}` },
  }));
  const next = start + notes.length;
  return { notes, cursor: String(next), has_more: next < 75 };
}

/**
 * 连续调用 account.pick 时**避开上一次选的账号**，便于本地演示「源 → 目标」：
 * 第一次给 A，第二次给 B，第三次又给 A……否则容易出现「源=目标」卡住。
 */
let pickIndex = 1;

type MockShushuPost = {
  id: string; title: string; cover: string; publishedAt: number;
  visibility: 'public' | 'private'; backedUp: boolean; deleted: boolean; metrics: Record<string, number>;
};
const mockPosts = new Map<string, MockShushuPost[]>();
const mockHistory: { id: string; taskId: string; name: string; status: string; at: number; error?: string }[] = [];
let mockRunning = false;
/** 各账号最近一次笔记操作快照（`notes.state {accountId}`） */
const mockNotesSnapshots = new Map<string, Record<string, unknown>>();
let mockAllStopped = false;

function shushuPosts(accountId: string): MockShushuPost[] {
  if (!mockPosts.has(accountId)) {
    mockPosts.set(accountId, Array.from({ length: 6 }, (_, index) => ({
      id: `${accountId}:demo-${index + 1}`,
      title: `演示笔记 ${index + 1}`,
      cover: '',
      publishedAt: Date.now() - index * 86_400_000,
      visibility: index % 3 === 0 ? 'private' : 'public',
      backedUp: index < 4,
      deleted: false,
      metrics: { views: 1200 - index * 150, likes: 60 - index * 7, comments: index === 3 ? 0 : 12 - index },
    })));
  }
  return mockPosts.get(accountId)!;
}

/** 与原生 `PostAction.applies` 同规则 */
function noteActionApplies(action: string, post: MockShushuPost): boolean {
  if (post.deleted) return false;
  if (action === 'hide') return post.visibility !== 'private';
  if (action === 'unhide') return post.visibility === 'private';
  return action === 'delete';
}

const MOCK_NOTE_ACTIONS = [
  { key: 'hide', label: '设为仅自己可见', detail: '其他人看不到，随时可以改回公开', destructive: false,
    confirm: { title: '设为仅自己可见', message: '将把{target}设为仅自己可见，其他人将看不到。', confirmLabel: '设为仅自己可见' } },
  { key: 'unhide', label: '设为公开', detail: '恢复所有人可见', destructive: false,
    confirm: { title: '设为公开', message: '将把{target}设为公开，所有人都能看到。', confirmLabel: '设为公开' } },
  { key: 'delete', label: '删除笔记', detail: '从平台上删除，不可恢复；本机备份会保留', destructive: true,
    confirm: { title: '删除笔记', message: '将从平台上删除{target}，此操作不可恢复。本机已备份的内容会保留。', confirmLabel: '删除' } },
];

function emit(event: string, payload: unknown) {
  window.__bridgeEmit?.({ event, payload });
}

// ---------------------------------------------------------------------------
// 假任务：按真实语义推进度事件
// ---------------------------------------------------------------------------

type FakeRun = {
  timer: number | null;
  accountId: string;
  taskId: string;
  step: number;
  total: number;
  event: string;
  snapshot: (state: string, step: number) => Record<string, unknown>;
};

let backupRun: FakeRun | null = null;
let migrationRun: FakeRun | null = null;
let notesRun: FakeRun | null = null;
/** 演示用：被标记为「仅自己可见」的笔记 id */
const mockPrivateNotes = new Set<string>();

const STEP_MS = 500;

function stopRun(run: FakeRun | null) {
  if (run?.timer != null) {
    window.clearInterval(run.timer);
    run.timer = null;
  }
}

function startRun(run: FakeRun) {
  run.timer = window.setInterval(() => {
    run.step += 1;
    if (run.step >= run.total) {
      stopRun(run);
      emit(run.event, run.snapshot('completed', run.total));
      return;
    }
    emit(run.event, run.snapshot('running', run.step));
  }, STEP_MS);
  emit(run.event, run.snapshot('running', run.step));
}

const backupSnapshot = (accountId: string, taskId: string) =>
  (state: string, step: number) => ({
    accountId,
    taskId,
    state,
    progress: Math.min(1, step / 8),
    processed: step * 12,
  });

const migrationSnapshot = (accountId: string, taskId: string) =>
  (state: string, step: number) => ({
    accountId,
    taskId,
    state,
    counts: { total: 40, migrated: Math.min(40, step * 5), failed: 0, pending: Math.max(0, 40 - step * 5) },
  });

/** 笔记处理（百宝箱）：按真实语义逐条推进 notes.progress。 */
const notesSnapshot = (ids: string[]) =>
  (state: string, step: number) => ({
    state,
    total: ids.length,
    done: state === 'completed' ? ids.length : step,
    failed: 0,
    /** 本批次已成功的 id（累计），页面据此就地把状态翻成「已隐藏」 */
    doneIds: ids.slice(0, state === 'completed' ? ids.length : step),
  });

let mockTasks = [
  {
    taskId: 'mock-backup-1',
    type: 'backup',
    name: '备份 · 笔记',
    status: 'completed',
    accountId: 'mock-1',
    accountNickname: '演示账号 A',
    current: 96,
    total: 96,
    successCount: 96,
    failedCount: 0,
    createdAt: Date.now() - 3_600_000,
  },
  {
    taskId: 'mock-migration-1',
    type: 'migration',
    name: '账号迁移',
    status: 'paused',
    accountId: 'mock-1',
    accountNickname: '演示账号 A',
    current: 12,
    total: 40,
    successCount: 12,
    failedCount: 1,
    createdAt: Date.now() - 600_000,
  },
];

function setTaskStatus(taskId: string, status: string) {
  mockTasks = mockTasks.map((t) => (t.taskId === taskId ? { ...t, status } : t));
  emit('task.changed', null);
}

const handlers: Record<string, MockHandler> = {
  'app.info': () => ({
    platform: 'android',
    appVersion: '1.0.1',
    buildNumber: '68',
    bridgeVersion: 6,
    locale: 'zh-CN',
    theme: 'light',
  }),
  'app.ready': () => null,
  'app.toast': () => null,
  'app.haptic': (params) => {
    console.log('[mock:haptic]', params.style);
    return null;
  },
  'app.setTitle': () => null,
  'app.log': (params) => {
    console.log('[mock:app.log]', params.level, params.message);
    return null;
  },
  'app.close': () => null,
  'app.openUrl': (params) => {
    window.open(String(params.url), '_blank');
    return null;
  },
  'app.openNative': (params) => {
    console.log('[mock:app.openNative]', params.page, params.params);
    return null;
  },
  'account.list': () => MOCK_ACCOUNTS,
  'account.get': (params) => {
    const found = MOCK_ACCOUNTS.find((a) => a.id === params.accountId);
    if (!found) throw new BridgeError('account_not_found', `账号不存在: ${params.accountId}`);
    return found;
  },
  // 从 B 开始轮换：页面通常已把 A 作为源账号预选，第一次 pick（目标）给 B 才不会撞。
  'account.pick': () => MOCK_ACCOUNTS[pickIndex++ % MOCK_ACCOUNTS.length],
  'local.query': () => ({ items: [], total: 0 }),

  'backup.start': (params) => {
    const accountId = String(params.accountId ?? '');
    const taskId = `mock-backup-${Date.now()}`;
    stopRun(backupRun);
    backupRun = {
      timer: null,
      accountId,
      taskId,
      step: 0,
      total: 8,
      event: 'backup.progress',
      snapshot: backupSnapshot(accountId, taskId),
    };
    startRun(backupRun);
    return backupRun.snapshot('running', 0);
  },
  'backup.status': (params) => {
    if (backupRun) return backupRun.snapshot('running', backupRun.step);
    const existing = mockTasks.find(
      (t) => t.type === 'backup' && (!params.accountId || t.accountId === params.accountId),
    );
    if (!existing) return { state: 'idle' };
    return {
      accountId: existing.accountId,
      taskId: existing.taskId,
      state: existing.status,
      progress: existing.total ? existing.current / existing.total : 0,
      processed: existing.current,
    };
  },
  'backup.pause': () => {
    if (!backupRun) return { state: 'idle' };
    stopRun(backupRun);
    const snap = backupRun.snapshot('paused', backupRun.step);
    emit('backup.progress', snap);
    return snap;
  },
  'backup.resume': () => {
    if (!backupRun) return { state: 'idle' };
    startRun(backupRun);
    return backupRun.snapshot('running', backupRun.step);
  },

  'migration.start': (params) => {
    const accountId = String(params.accountId ?? '');
    const taskId = `mock-migration-${Date.now()}`;
    stopRun(migrationRun);
    if (!params.skipPrefetch) {
      // 先走一段「补拉中」，模拟迁移前的增量补拉
      emit('migration.progress', {
        accountId,
        state: 'preparing',
        note: '正在增量补拉源账号数据…',
      });
      window.setTimeout(() => {
        migrationRun = {
          timer: null,
          accountId,
          taskId,
          step: 0,
          total: 8,
          event: 'migration.progress',
          snapshot: migrationSnapshot(accountId, taskId),
        };
        startRun(migrationRun);
      }, 1200);
      return { accountId, taskId, state: 'preparing' };
    }
    migrationRun = {
      timer: null,
      accountId,
      taskId,
      step: 0,
      total: 8,
      event: 'migration.progress',
      snapshot: migrationSnapshot(accountId, taskId),
    };
    startRun(migrationRun);
    return migrationRun.snapshot('running', 0);
  },
  'migration.state': (params) => {
    if (migrationRun) return migrationRun.snapshot('running', migrationRun.step);
    const existing = mockTasks.find(
      (t) => t.type === 'migration' && (!params.accountId || t.accountId === params.accountId),
    );
    if (!existing) return { state: 'idle' };
    return {
      accountId: existing.accountId,
      taskId: existing.taskId,
      state: existing.status,
      counts: {
        total: existing.total,
        migrated: existing.successCount,
        failed: existing.failedCount,
        pending: existing.total - existing.successCount - existing.failedCount,
      },
    };
  },
  'migration.pause': () => {
    if (!migrationRun) return { state: 'idle' };
    stopRun(migrationRun);
    const snap = migrationRun.snapshot('paused', migrationRun.step);
    emit('migration.progress', snap);
    return snap;
  },
  'migration.resume': () => {
    if (!migrationRun) return { state: 'idle' };
    startRun(migrationRun);
    return migrationRun.snapshot('running', migrationRun.step);
  },

  'task.list': () => ({ tasks: mockTasks }),
  'task.get': (params) => {
    const t = mockTasks.find((x) => x.taskId === params.taskId);
    if (!t) throw new BridgeError('unknown', `任务不存在: ${params.taskId}`);
    return {
      task: t,
      summary: {
        total: t.total,
        migrated: t.successCount,
        failed: t.failedCount,
        pending: t.total - t.successCount - t.failedCount,
      },
      hasItems: t.type === 'migration',
    };
  },
  'task.items': (params) => {
    if (params.failedOnly) {
      return {
        total: 3,
        failedTotal: 1,
        returned: 1,
        items: [
          {
            id: 'item-failed-1',
            title: '失败的笔记示例',
            type: '笔记',
            status: 'failed',
            error: '离线视频缺失',
          },
        ],
      };
    }
    return {
      total: 3,
      failedTotal: 1,
      returned: 3,
      items: [
        { id: 'item-1', title: '笔记一', type: '笔记', status: 'migrated' },
        { id: 'item-2', title: '关注的用户 A', type: '关注', status: 'migrated' },
        {
          id: 'item-failed-1',
          title: '失败的笔记示例',
          type: '笔记',
          status: 'failed',
          error: '离线视频缺失',
        },
      ],
    };
  },
  'task.pause': (params) => {
    setTaskStatus(String(params.taskId), 'paused');
    return { tasks: mockTasks };
  },
  'task.resume': (params) => {
    if (params.taskId === 'mock-all') {
      // 整账号批量续跑：对同一账号同一操作重新跑（已处理的已不适用，会被跳过）
      const last = [...mockNotesSnapshots.values()].find((snap) => snap.taskId === 'mock-all');
      if (last) handlers['notes.run']({ accountId: last.accountId, action: last.action, scope: 'all' });
      return { tasks: mockTasks };
    }
    setTaskStatus(String(params.taskId), 'running');
    return { tasks: mockTasks };
  },
  'task.delete': (params) => {
    mockTasks = mockTasks.filter((t) => t.taskId !== params.taskId);
    emit('task.changed', null);
    return { tasks: mockTasks };
  },

  'notes.list': (params) => {
    const limit = Number(params.limit ?? 500);
    const count = Math.min(5, limit);
    const items = Array.from({ length: count }, (_, index) => {
      const awemeId = `mock-note-${index + 1}`;
      return {
        awemeId,
        desc: `演示笔记 ${index + 1}：一段用于本地调试的作品描述`,
        cover: '',
        createdAt: new Date(Date.now() - index * 86_400_000).toISOString(),
        // 演示：默认第 3 条已隐藏；点「同步状态」后第 3、5 条隐藏
        isPrivate: index === 2 || mockPrivateNotes.has(awemeId),
        statistics: {
          play: 11729 - index * 1500,
          digg: 10 - index,
          comment: Math.max(0, 3 - index),
          share: 2,
          collect: index === 0 ? 2 : 0,
        },
        metrics: {
          completion_rate_5s: 0.549 - index * 0.06,
          bounce_rate_2s: 0.328 + index * 0.04,
          avg_view_second: 9.4 - index * 0.8,
          homepage_visit_count: Math.max(0, 141 - index * 30),
        },
      };
    });
    return {
      total: items.length,
      offset: Number(params.offset ?? 0),
      visibilityUpdatedAt: mockPrivateNotes.size ? new Date().toISOString() : null,
      items,
    };
  },
  'notes.refreshVisibility': () => {
    mockPrivateNotes.clear();
    ['mock-note-3', 'mock-note-5'].forEach((id) => mockPrivateNotes.add(id));
    return {
      total: 5,
      privateCount: mockPrivateNotes.size,
      visibilityUpdatedAt: new Date().toISOString(),
    };
  },
  'notes.setPrivate': (params) => {
    const ids = Array.isArray(params.awemeIds) ? params.awemeIds.map(String) : [];
    stopRun(notesRun);
    // 让重进页面时 list 也反映新状态（实时进度则由 doneIds 驱动）
    ids.forEach((id) => mockPrivateNotes.add(id));
    notesRun = {
      timer: null,
      accountId: '',
      taskId: '',
      step: 0,
      total: Math.max(ids.length, 1),
      event: 'notes.progress',
      snapshot: notesSnapshot(ids),
    };
    startRun(notesRun);
    return notesRun.snapshot('running', 0);
  },
  'notes.state': (params) => {
    if (params.accountId) return mockNotesSnapshots.get(String(params.accountId)) ?? { state: 'idle' };
    return notesRun ? notesRun.snapshot('running', notesRun.step) : { state: 'idle' };
  },
  'notes.cancel': () => {
    stopRun(notesRun);
    const snapshot = notesRun?.snapshot('cancelled', notesRun.step) ?? { state: 'cancelled' };
    emit('notes.progress', snapshot);
    return snapshot;
  },

  'data.overview': (params) => {
    const recentDays = Number(params.recentDays ?? 7);
    const dateKey = (offset: number) => {
      const d = new Date(Date.now() - offset * 86_400_000);
      return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
    };
    const metric = (key: string, name: string, base: number, ratio = false) => ({
      key,
      name,
      value: ratio ? +(base / 100).toFixed(4) : base,
      trends: Array.from({ length: recentDays }, (_, index) => {
        const jitter = index % 4;
        return {
          date: dateKey(recentDays - 1 - index),
          value: ratio
            ? Number((base / 100 + jitter * 0.03).toFixed(4))
            : base + jitter * 17,
          changeRate: index === 0 ? null : jitter * 0.1 - 0.2,
        };
      }),
    });
    return {
      recentDays,
      metrics: [
        metric('play_cnt', '播放量', 430),
        metric('digg_cnt', '作品点赞', 12),
        metric('comment_cnt', '作品评论', 3),
        metric('share_count', '作品分享', 5),
        metric('publish_cnt', '投稿量', 63),
        metric('completion_rate_5s', '5秒完播率', 40, true),
        metric('bounce_rate_2s', '2秒跳出率', 43, true),
        metric('cover_click_ratio', '封面点击率', 29, true),
        metric('avg_view_second', '平均播放时长', 10.23),
        metric('homepage_view_cnt', '主页访问', 26),
        metric('total_fans_cnt', '总粉丝量', 32),
        metric('net_fans_cnt', '净增粉丝', 3),
        metric('cancel_fans_cnt', '取关粉丝', 0),
      ],
    };
  },
  'data.works': () => ({
    hasMore: false,
    total: 5,
    cursor: 0,
    items: Array.from({ length: 5 }, (_, index) => ({
      awemeId: `mock-work-${index + 1}`,
      desc: `演示作品 ${index + 1}：一段用于本地调试的作品描述`,
      cover: '',
      createdAt: Date.now() - index * 86_400_000,
      statistics: {
        play: 157 - index * 24,
        digg: 6 - index,
        comment: 3,
        share: 1,
        collect: 0,
      },
      // 与真实接口一致的深度指标（比率是 0~1 小数）
      metrics: {
        completion_rate: 0.172619 - index * 0.02,
        bounce_rate_2s: 0.426966 + index * 0.03,
        cover_click_rate: index % 2 === 0 ? 0.045 : null,
        avg_view_second: 4.061798 - index * 0.4,
        homepage_visit_count: Math.max(0, 3 - index),
        subscribe_count: index === 0 ? 2 : 0,
        unsubscribe_count: 0,
      },
    })),
  }),

  // 薯薯 · 数据分析（形状与原生 AnalyticsBridgeModule 一致）
  'analytics.overview': (params) => {
    const recentDays = Number(params.recentDays) === 30 ? 30 : 7;
    const day = (offset: number) => new Date(Date.now() - offset * 86_400_000).toISOString().slice(0, 10);
    const metric = (
      key: string,
      title: string,
      format: string,
      aggregation: string,
      daily: (i: number) => number,
      lowerIsBetter = false,
    ) => {
      const trend = Array.from({ length: recentDays }, (_, i) => ({
        date: day(recentDays - 1 - i),
        value: daily(i),
      }));
      const values = trend.map((t) => t.value);
      const value =
        aggregation === 'sum'
          ? values.reduce((a, b) => a + b, 0)
          : aggregation === 'average'
            ? values.reduce((a, b) => a + b, 0) / values.length
            : values[values.length - 1];
      return { key, title, format, aggregation, lowerIsBetter, value, previous: value * 0.85, trend };
    };
    return {
      accountId: params.accountId,
      platform: 'douyin',
      recentDays,
      updatedAt: Date.now() - 5 * 60_000,
      metrics: [
        metric('daily_views', '播放量', 'count', 'sum', (i) => 800 + (i % 4) * 90),
        metric('daily_likes', '作品点赞', 'count', 'sum', (i) => 24 + (i % 3) * 4),
        metric('daily_comments', '作品评论', 'count', 'sum', (i) => 6 + (i % 2)),
        metric('completion_rate_5s', '5秒完播率', 'percent', 'average', (i) => 0.36 + (i % 3) * 0.02),
        metric('bounce_rate_2s', '2秒跳出率', 'percent', 'average', (i) => 0.44 - (i % 3) * 0.01, true),
        metric('avg_view_seconds', '平均播放时长', 'seconds', 'average', (i) => 9.6 + (i % 4) * 0.3),
        metric('followers', '总粉丝', 'count', 'latest', (i) => 3160 + i * 3),
        metric('net_followers', '净增粉丝', 'signedCount', 'sum', (i) => (i % 5) - 1),
      ],
    };
  },
  'analytics.works': () => ({
    items: Array.from({ length: 5 }, (_, index) => ({
      id: `mock-work-${index + 1}`,
      title: `演示作品 ${index + 1}：一段用于本地调试的作品标题`,
      cover: '',
      url: '',
      type: 'video',
      publishedAt: Date.now() - index * 4 * 86_400_000,
      metrics: {
        views: 2400 - index * 380,
        likes: 98 - index * 15,
        comments: 21 - index * 3,
        collects: 64 - index * 10,
        ...(index < 4 ? { completion_rate: 0.31 - index * 0.04, bounce_rate_2s: 0.33 + index * 0.05, homepage_visits: 15 - index * 3 } : {}),
      },
    })),
  }),

  // 薯薯 · 备份 / 迁移的类型目录（形状与原生 BackupBridgeModule / MigrationBridgeModule 一致）。
  // mock 返回 supported=true，方便在浏览器里走完整流程；原生目前回 false +「即将支持」。
  'backup.types': () => ({
    supported: true,
    types: [{ key: 'posts', label: '笔记', count: 56 }],
  }),
  'migration.types': () => ({
    supported: true,
    types: [{ key: 'posts', label: '笔记', count: 56 }],
  }),

  // 薯薯 · 笔记与笔记操作（形状与原生 NotesBridgeModule 一致；有状态，操作会改到列表）
  'notes.posts': (params) => {
    const all = shushuPosts(String(params.accountId));
    const items = params.includeDeleted ? all : all.filter((p) => !p.deleted);
    return { total: items.length, offset: 0, items };
  },
  'notes.get': (params) => {
    const post = shushuPosts(String(params.accountId)).find((p) => p.id === params.id);
    if (!post) throw new BridgeError('invalid_params', '笔记不存在');
    const { metrics: _metrics, ...rest } = post;
    return {
      ...rest,
      type: 'note',
      url: 'https://www.xiaohongshu.com/explore/demo',
      history: mockHistory.filter((h) => h.id === post.id).map(({ id: _id, ...h }) => h),
    };
  },
  'notes.actions': (params) => {
    const posts = shushuPosts(String(params.accountId));
    if (params.scope === 'all') {
      const live = posts.filter((p) => !p.deleted);
      return {
        actions: MOCK_NOTE_ACTIONS.map((action) => {
          const applicable = live.filter((p) => noteActionApplies(action.key, p));
          const unbacked = applicable.filter((p) => !p.backedUp).length;
          const label = action.key === 'delete' ? '删除全部笔记' : action.key === 'hide' ? '全部设为仅自己可见' : '全部设为公开';
          return {
            ...action,
            label,
            applicable: applicable.length,
            unbacked,
            checklist: action.destructive
              ? [
                  '我了解这将从平台删除该账号的全部笔记，包括本机还没同步到的',
                  unbacked > 0
                    ? `我了解删除后无法在平台恢复，其中 ${unbacked} 篇本机没有备份，将彻底丢失`
                    : '我了解删除后无法在平台恢复，本机备份会保留',
                  '我确认要删除这些笔记',
                ]
              : [],
            confirm: { title: label, message: `将逐页读取线上笔记并处理。本机已同步 ${applicable.length} 篇，线上可能更多。`, confirmLabel: label },
            ...(mockRunning ? { disabledReason: '有笔记操作正在进行' } : {}),
          };
        }),
      };
    }
    const selected = posts.filter((p) => ((params.ids as string[]) ?? []).includes(p.id));
    return {
      actions: MOCK_NOTE_ACTIONS.map((action) => {
        const applicable = selected.filter((p) => noteActionApplies(action.key, p));
        const target = applicable.length === 1 ? `「${applicable[0].title}」` : `选中的 ${applicable.length} 篇笔记`;
        return {
          ...action,
          applicable: applicable.length,
          confirm: { ...action.confirm, message: action.confirm.message.replace('{target}', target) },
          ...(mockRunning ? { disabledReason: '有笔记操作正在进行' } : {}),
        };
      }).concat(
        // 清空评论：只对单篇（与原生 commentActions 一致）
        selected.length === 1 && !selected[0].deleted
          ? [{
              key: 'clearComments', label: '清空评论', detail: '删除这篇的全部 8 条评论（含回复），不可恢复',
              destructive: true, applicable: 1,
              confirm: { title: '清空评论', message: `将删除「${selected[0].title}」的全部一级评论，每条的回复会一起删除，此操作不可恢复。`, confirmLabel: '清空评论' },
              ...(mockRunning ? { disabledReason: '有笔记操作正在进行' } : {}),
            }]
          : [],
      ),
    };
  },
  'notes.run': (params) => {
    if (params.action === 'clearComments') {
      // 演示：8 条评论逐条删，第 5 条固定失败、反复删不掉 → 「卡住自动停」
      const accountId = String(params.accountId);
      mockRunning = true;
      const snap = { accountId, action: 'clearComments', taskId: 'mock-comments', state: 'running', total: 8, done: 0, failed: 0, doneIds: [] as string[] };
      Array.from({ length: 8 }, (_, index) =>
        window.setTimeout(() => {
          if (index === 4) snap.failed += 1;
          else snap.done += 1;
          const last = index === 7;
          if (last) mockRunning = false;
          mockHistory.unshift(...(last ? [{ id: String((params.ids as string[])[0]), taskId: 'mock-comments', name: '清空评论', status: 'done', at: Date.now() }] : []));
          emit('notes.progress', {
            ...snap, state: last ? 'completed' : 'running',
            ...(last ? { note: '还有 1 条评论反复删除不成功（平台侧限制），已自动停止，可以过一会儿再清一次' } : {}),
          });
        }, (index + 1) * STEP_MS),
      );
      return { ...snap };
    }
    const posts = shushuPosts(String(params.accountId));
    const action = String(params.action);
    if (params.scope === 'all') {
      if (mockRunning) throw new BridgeError('failed', '已有笔记操作在进行中，请等它完成');
      // 演示整账号批量：逐篇处理，不适用的跳过；`?fail=rate_limited` 时第 3 篇停下（可继续）
      const accountId = String(params.accountId);
      const live = posts.filter((p) => !p.deleted);
      const stopAt = !mockAllStopped && new URLSearchParams(window.location.search).get('fail') === 'rate_limited' ? 2 : -1;
      if (stopAt >= 0) mockAllStopped = true;
      const snap = { accountId, action, taskId: 'mock-all', scope: 'all', state: 'running', total: live.length, done: 0, failed: 0, skipped: 0, doneIds: [] as string[] } as Record<string, unknown> & { done: number; skipped: number; doneIds: string[] };
      mockRunning = true;
      mockNotesSnapshots.set(accountId, { ...snap });
      live.forEach((post, index) =>
        window.setTimeout(() => {
          if (!mockRunning) return;
          if (index === stopAt) {
            mockRunning = false;
            Object.assign(snap, { state: 'failed', error: '操作太频繁，请稍后再试' });
          } else {
            if (!noteActionApplies(action, post)) snap.skipped += 1;
            else {
              if (action === 'hide') post.visibility = 'private';
              if (action === 'unhide') post.visibility = 'public';
              if (action === 'delete') post.deleted = true;
              snap.done += 1;
              snap.doneIds.push(post.id);
            }
            if (index === live.length - 1) {
              mockRunning = false;
              Object.assign(snap, { state: 'completed', total: snap.done });
            }
          }
          mockNotesSnapshots.set(accountId, { ...snap, doneIds: [...snap.doneIds] });
          emit('notes.progress', { ...snap, doneIds: [...snap.doneIds] });
        }, (index + 1) * STEP_MS),
      );
      return { ...snap };
    }
    const targets = posts.filter((p) => ((params.ids as string[]) ?? []).includes(p.id) && noteActionApplies(action, p));
    if (!targets.length) throw new BridgeError('failed', '选中的笔记都不需要这个操作');
    mockRunning = true;
    const doneIds: string[] = [];
    let failed = 0;
    targets.forEach((post, index) =>
      window.setTimeout(() => {
        // 演示：删除第 3 篇固定失败
        const fail = action === 'delete' && post.id.endsWith('demo-3');
        if (fail) failed += 1;
        else {
          if (action === 'hide') post.visibility = 'private';
          if (action === 'unhide') post.visibility = 'public';
          if (action === 'delete') post.deleted = true;
          doneIds.push(post.id);
        }
        const name = MOCK_NOTE_ACTIONS.find((a) => a.key === action)?.label ?? action;
        mockHistory.unshift({ id: post.id, taskId: 'mock-task', name, status: fail ? 'failed' : 'done', at: Date.now(), ...(fail ? { error: '演示：网络波动，删除失败' } : {}) });
        const last = index === targets.length - 1;
        if (last) mockRunning = false;
        emit('notes.progress', {
          accountId: params.accountId, action, taskId: 'mock-task',
          state: last ? 'completed' : 'running',
          total: targets.length, done: doneIds.length, failed, doneIds: [...doneIds],
        });
      }, (index + 1) * STEP_MS),
    );
    return { accountId: params.accountId, action, taskId: 'mock-task', state: 'running', total: targets.length, done: 0, failed: 0, doneIds: [] };
  },
  'notes.setVisibility': (params) =>
    handlers['notes.run']({ ...params, action: params.private ? 'hide' : 'unhide' }),
  'app.confirm': (params) => ({
    confirmed: window.confirm([params.title, params.message].filter(Boolean).join('\n\n')),
  }),

  'app.readClipboard': () => ({ text: 'http://xhslink.com/a/demo 复制后打开【小红书】查看笔记！' }),
  'app.writeClipboard': (params) => {
    console.log('[mock:clipboard]', String(params.text).slice(0, 200));
    return null;
  },
  'app.share': (params) => {
    console.log('[mock:share]', params);
    return null;
  },
  'app.resolveUrl': (params) => ({
    url: String(params.url).includes('xhslink')
      ? 'https://www.xiaohongshu.com/discovery/item/6650a1b2000000001e02f3c4?xsec_token=demo&xsec_source=app_share'
      : params.url,
  }),
  'xhs.request': (params) => {
    const path = String(params.path);
    if (path === '/api/sns/web/v1/feed') return { data: mockFeed(String((params.body as Record<string, unknown>)?.source_note_id)) };
    if (path === '/api/sns/web/v1/user/selfinfo') return { data: { follows: 4, tab_public: { collectionNote: { count: 75 } } } };
    if (path === '/api/sns/web/v1/note/like/page') {
      // 点赞：只有 6 条，取消一轮后清空
      const left = mockLikesLeft;
      mockLikesLeft = 0;
      return { data: { notes: Array.from({ length: left }, (_, i) => ({ note_id: `like-${i}` })), has_more: false } };
    }
    if (path === '/api/sns/web/v1/intimacy/intimacy_list') {
      const left = mockFollowsLeft;
      mockFollowsLeft = 0;
      return { data: { items: Array.from({ length: left }, (_, i) => ({ userid: `u${i}`, follow_status: 1 })) } };
    }
    if (path === '/api/sns/web/v2/note/collect/page') {
      return { data: mockCollectPage(String((params.query as Record<string, unknown>)?.cursor ?? '')) };
    }
    throw new BridgeError('invalid_params', 'path not allowed');
  },
  'media.save': (params) => ({ saved: (params.items as unknown[]).length, failed: 0 }),
  'xhs.unlikeNote': (params) => ({ noteId: params.noteId }),
  'xhs.uncollectNote': (params) => ({ noteId: params.noteId }),
  'xhs.unfollowUser': (params) => ({ userId: params.userId }),
  'comments.state': (params) => ({
    ...mockCommentBackup(String(params.accountId)),
    stats: { targets: 5, notes: mockCommentNotes, comments: mockCommentNotes * 9 },
  }),
  'comments.start': (params) => startMockCommentBackup(String(params.accountId)),
  'comments.list': () => ({
    items: Array.from({ length: 9 }, (_, i) => ({
      id: `c${i}`, author: `演示用户 ${i + 1}`, text: i % 3 === 0 ? '写得好清楚，收藏了！\n第二行也看看' : `演示评论 ${i + 1}`,
      replyCount: i % 3, likeCount: i * 2, createdAt: Date.now() - i * 3_600_000, fetchedAt: Date.now() - 600_000,
    })),
  }),
  'export.summary': () => ({ notes: 4, bytes: 38_500_000 }),
  'export.posts': () => ({ notes: 4, bytes: 37_900_000 }),

  'track.event': () => null,
  'track.pageOpen': () => null,
};

function shouldInstall(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  // 远程桥（?bridge=）优先：连真实 App，不要装假桥抢占
  if (params.has('bridge') && !params.has('mock')) return false;
  if (import.meta.env.PROD && !params.has('mock')) {
    return false;
  }
  return import.meta.env.DEV || params.has('mock');
}

export function installBridgeMock(): boolean {
  if (!shouldInstall()) return false;
  if (window.flutter_inappwebview?.callHandler) return false;

  window.flutter_inappwebview = {
    callHandler: (name: string, ...args: unknown[]) => {
      const message = args[0] as {
        method?: string;
        requestId?: string;
        params?: Record<string, unknown>;
      };
      const requestId = message?.requestId ?? '';
      const handler = message?.method ? handlers[message.method] : undefined;
      console.log('[mock:bridge]', name, message?.method, message?.params);

      if (!handler) {
        return Promise.resolve({
          requestId,
          ok: false,
          error: 'unknown_method',
          message: `mock 未实现: ${message?.method}`,
        });
      }

      try {
        return Promise.resolve({ requestId, ok: true, data: handler(message?.params ?? {}) });
      } catch (error) {
        const code = error instanceof BridgeError ? error.code : 'unknown';
        return Promise.resolve({ requestId, ok: false, error: code, message: String(error) });
      }
    },
  };
  console.info('[bridgeMock] 已安装假桥（?mock=1）');
  return true;
}
